// Author: Sunil Jain & Gemini
// File: src/utils/authFetch.js
// Description: FINAL, STABLE VERSION. Handles URL construction, JSON serialization,
// token refresh, and dynamic tenant context overrides.

import logger from './logger';

// --- Refresh token logic is now self-contained to prevent circular dependencies ---
const refreshToken = async () => {
  const currentRefreshToken = localStorage.getItem('refreshToken');
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';

  if (!currentRefreshToken) {
    logger.error('refreshToken: No refresh token available.');
    return null;
  }

  try {
    const response = await fetch(`${apiBaseUrl}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: currentRefreshToken }),
    });

    if (!response.ok) {
      logger.error('refreshToken: Failed to refresh token, forcing re-login.');
      localStorage.clear(); // Clear all auth storage
      window.location.href = '/login';
      return null;
    }

    const data = await response.json();
    localStorage.setItem('accessToken', data.access_token);
    if (data.refresh_token) {
        localStorage.setItem('refreshToken', data.refresh_token);
    }
    logger.info("refreshToken: Token refreshed successfully.");
    return data.access_token;
  } catch (error) {
    logger.error('refreshToken: Exception during token refresh, forcing re-login.', error);
    localStorage.clear();
    window.location.href = '/login';
    return null;
  }
};

const handleResponse = async (response) => {
    if (!response.ok) {
        let errorData;
        try { errorData = await response.json(); } catch (e) {
             const error = new Error(`API call failed: ${response.status} ${response.statusText}`);
             error.status = response.status;
             throw error;
        }
        const error = new Error(errorData.detail || `API error: ${response.status}`);
        error.data = errorData;
        error.status = response.status;
        throw error;
    }
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
        const text = await response.text();
        return text ? JSON.parse(text) : {};
    }
    return;
};

// [CRITICAL FIX] Added 'tenantId' to the destructuring here so it is defined in scope
const authFetch = async (endpoint, fetchOptions = {}, options = {}) => {
    const { isPublic = false, tenantId = null } = options; 
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';

    // --- URL Construction Logic ---
    let finalURL = endpoint;
    // Check if the endpoint is a full URL already
    if (!endpoint.startsWith('http')) {
        // Ensure the base URL and endpoint paths do not create a double slash
        const a = apiBaseUrl.endsWith('/') ? apiBaseUrl.slice(0, -1) : apiBaseUrl;
        const b = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
        finalURL = `${a}/${b}`;
    }

    // --- Body Stringification Logic ---
    if (fetchOptions.body && typeof fetchOptions.body === 'object' && !(fetchOptions.body instanceof FormData) && !(fetchOptions.body instanceof URLSearchParams)) {
        if (!fetchOptions.headers || !fetchOptions.headers['Content-Type']) {
            fetchOptions.headers = { ...fetchOptions.headers, 'Content-Type': 'application/json' };
        }
        fetchOptions.body = JSON.stringify(fetchOptions.body);
    }
    
    // --- Public Route Execution ---
    if (isPublic) {
        const publicResponse = await fetch(finalURL, fetchOptions);
        return handleResponse(publicResponse);
    }

    // --- Protected Route Execution ---
    let accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
        window.location.href = '/login';
        throw new Error('No access token available.');
    }
    
    const headers = { ...fetchOptions.headers, 'Authorization': `Bearer ${accessToken}` };
    
    // [PRAXIS FIX] Dynamic Tenant Context Priority:
    // 1. Explicit override provided in options (e.g. for fetching branding of a specific tenant URL)
    // 2. Local Storage 'selectedTenant' (the user's current session context)
    if (tenantId) {
        headers['X-Tenant-ID'] = tenantId;
    } else {
        const selectedTenantString = localStorage.getItem('selectedTenant');
        if (selectedTenantString) {
            try {
                const tenant = JSON.parse(selectedTenantString);
                if (tenant?.tenant_id) headers['X-Tenant-ID'] = tenant.tenant_id;
            } catch (e) { /* ignore */ }
        }
    }
    
    let response = await fetch(finalURL, { ...fetchOptions, headers });

    if (response.status === 401) {
        const newAccessToken = await refreshToken();
        if (newAccessToken) {
            headers['Authorization'] = `Bearer ${newAccessToken}`;
            response = await fetch(finalURL, { ...fetchOptions, headers });
        } else {
             throw new Error('Token refresh failed.');
        }
    }
    return handleResponse(response);
};

export default authFetch;